import type { DiscordAccount, DiscordCodeInfo, MapVoiceSession, TeamDiscord, VoiceSessionInfo, VoiceTrack } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";
import { matchKeys } from "./demo-queries.ts";

const keys = {
  me: ["discord", "me"] as const,
  code: (code: string) => ["discord", "code", code] as const,
  team: (id: string) => ["discord", "team", id] as const,
  sessions: (id: string) => ["discord", "sessions", id] as const,
  mapSessions: (demoId: string) => ["discord", "map-sessions", demoId] as const,
};

/** My linked Discord account, if any. */
export const useMyDiscord = () => useQuery({ queryKey: keys.me, queryFn: () => api<DiscordAccount | null>("GET", "/me/discord") });

/** What a link from the bot is for. */
export const useDiscordCode = (code: string) =>
  useQuery({ queryKey: keys.code(code), queryFn: () => api<DiscordCodeInfo>("GET", `/discord/codes/${encodeURIComponent(code)}`), enabled: !!code, retry: false });

export function useLinkDiscord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; consent: boolean }) => api<DiscordAccount>("POST", "/discord/link", input),
    onSuccess: (account) => {
      qc.setQueryData(keys.me, account);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export function useDiscordConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consent: boolean) => api<DiscordAccount>("PATCH", "/me/discord", { consent }),
    onSuccess: (account) => {
      qc.setQueryData(keys.me, account);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export function useUnlinkDiscord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", "/me/discord"),
    onSuccess: () => {
      qc.setQueryData(keys.me, null);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export const useLinkDiscordTeam = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; teamId: string }) => api<{ ok: true }>("POST", "/discord/team-link", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["discord", "team"] }),
  });
};

export const useTeamDiscord = (teamId: string) => useQuery({ queryKey: keys.team(teamId), queryFn: () => api<TeamDiscord>("GET", `/teams/${teamId}/discord`) });

export function useUnlinkDiscordTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", `/teams/${teamId}/discord`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.team(teamId) }),
  });
}

export const useVoiceSessions = (teamId: string) => useQuery({ queryKey: keys.sessions(teamId), queryFn: () => api<VoiceSessionInfo[]>("GET", `/teams/${teamId}/voice-sessions`) });

export function useDeleteVoiceSession(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/voice-sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions(teamId) }),
  });
}

/** The team's recordings made around this map's match. */
export const useMapVoiceSessions = (demoId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.mapSessions(demoId), queryFn: () => api<MapVoiceSession[]>("GET", `/demos/${demoId}/voice-sessions`), enabled });

export function useAddVoiceSession(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api<VoiceTrack[]>("POST", `/demos/${demoId}/voices/from-session`, { sessionId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: keys.mapSessions(demoId) });
    },
  });
}
